import { ActionIcon, Badge } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import type { TeamGameDay } from '../../shared/contracts';
import { dayAt, dayKey, weekStartOf } from '../../shared/format';
import { TeamLogo } from './TeamLogo';

interface Slot {
  week: string;
  day: TeamGameDay | null;
}

export function slotsOf(schedule: TeamGameDay[], today: string): Slot[] {
  const thisWeek = weekStartOf(today);
  const slots: Slot[] = schedule.map((day) => ({
    week: weekStartOf(day.date),
    day,
  }));
  if (!slots.some((slot) => slot.week === thisWeek)) {
    slots.push({ week: thisWeek, day: null });
  }
  return slots.sort((a, b) => a.week.localeCompare(b.week));
}

export function openingIndex(slots: Slot[], today: string): number {
  const nextGame = slots.findIndex(
    (slot) => slot.day && slot.day.date >= today,
  );
  if (nextGame !== -1) return nextGame;
  const thisWeek = weekStartOf(today);
  const next = slots.findIndex((slot) =>
    slot.day ? slot.day.date >= today : slot.week >= thisWeek,
  );
  return next === -1 ? slots.length - 1 : next;
}

function timeOf(startsAt: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/Dawson_Creek',
  }).format(new Date(startsAt));
}

export function GameDayMatches({ day }: { day: TeamGameDay }) {
  return (
    <div className="flex flex-col gap-2">
      {day.setUp || day.tearDown ? (
        <div className="flex flex-wrap items-center gap-2 px-1">
          {day.setUp ? (
            <Badge variant="light" color="gray" tt="none">
              Set-up
            </Badge>
          ) : null}
          {day.tearDown ? (
            <Badge variant="light" color="gray" tt="none">
              Tear-down
            </Badge>
          ) : null}
        </div>
      ) : null}
      <ul className="flex list-none flex-col gap-2 p-0">
        {day.matches.map((match) => (
          <li
            key={`${match.startsAt}-${match.court}-${match.round}`}
            className="bg-surface-soft flex items-center justify-between gap-4 rounded-lg px-3 py-2.5"
          >
            <div className="flex min-w-0 items-center gap-3">
              <TeamLogo
                teamId={match.opponentId}
                teamName={match.opponent}
                size="md"
              />
              <div className="flex flex-col items-start gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
                <span className="text-ink font-medium">
                  vs {match.opponent || 'To be announced'}
                </span>
                <span className="text-muted text-sm">{match.court}</span>
              </div>
            </div>
            <span className="text-body whitespace-nowrap tabular-nums">
              {timeOf(match.startsAt)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TeamSchedule({ schedule }: { schedule: TeamGameDay[] }) {
  const today = dayKey(Date.now());
  const slots = useMemo(() => slotsOf(schedule, today), [schedule, today]);
  const [index, setIndex] = useState(() => openingIndex(slots, today));
  const slot = slots[Math.min(index, slots.length - 1)]!;
  const day = slot.day;

  return (
    <>
      <div className="flex flex-col gap-2 px-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex min-w-0 flex-col items-center sm:items-start">
          <span className="text-ink font-medium sm:max-w-full sm:truncate">
            {day ? day.venue : ''}
          </span>
          <span className="text-muted text-sm sm:max-w-full sm:truncate">
            {day ? day.block : ''}
          </span>
        </div>
        <div className="flex shrink-0 flex-col items-center sm:items-end">
          <span className="text-ink font-medium">
            {day ? `Week ${day.week}` : 'This week'}
          </span>
          <span className="text-muted text-sm">
            {day ? dayAt(day.matches[0]!.startsAt) : 'Not posted yet'}
          </span>
        </div>
      </div>
      {day ? (
        <GameDayMatches day={day} />
      ) : (
        <p className="text-muted bg-surface-soft rounded-lg px-4 py-3">
          This week’s schedule hasn’t been posted yet. Use the arrows to see
          another week.
        </p>
      )}
      <div className="flex items-center justify-center gap-6">
        <ActionIcon
          variant="default"
          radius="xl"
          className="[--ai-size:2.75rem]! sm:[--ai-size:2.25rem]!"
          aria-label="Earlier week"
          disabled={index === 0}
          onClick={() => setIndex(index - 1)}
        >
          <IconChevronLeft size={22} />
        </ActionIcon>
        <ActionIcon
          variant="default"
          radius="xl"
          className="[--ai-size:2.75rem]! sm:[--ai-size:2.25rem]!"
          aria-label="Later week"
          disabled={index >= slots.length - 1}
          onClick={() => setIndex(index + 1)}
        >
          <IconChevronRight size={22} />
        </ActionIcon>
      </div>
    </>
  );
}
